import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchUserByEmail, logout } from "../../../../store/actions/userActions";
import { awaitingCoachVerification } from "../../../auth/ProtectedRoute";
import { TitleBlock, Note, Actions, PrimaryBtn, TextBtn } from "../../../auth/v2/pieces";
// The phone footer rules (.ob-frame .ob-actions) live with the onboarding
// frame's styles.
import "../../../auth/v2/onboarding.css";
import { Badge, Loading, Overline } from "../../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { initialsOf } from "../../../ForAthletes/v2/onboarding/onboardingPieces";
import { KeyValueRow, TeamCodeBox } from "../profile/coachProfilePieces";
import { getCoachOnboarding, saveCoachProgress } from "./coachOnboardingServices";
import { SettingsCard, VerificationHero } from "./coachOnboardingPieces";
import { leagueOf, verificationRows } from "./coachOnboardingModel";

// 09 · Awaiting verification (/coach/verification, inside Coach Mode). A
// pending coach lands here: the submitted team, and (Figma variants not
// drawn, following the proposal) the team code once verified, or "Not
// approved" with a way to send the team again. While pending it checks
// back every 30 seconds and when the window regains focus; a change of
// status refreshes the signed-in user so the route guards open or close.

const POLL_MS = 30000;
const LOCKED_NOTE = "CacheShare™ and the team pages stay locked until verification completes. You can edit your profile and check back any time.";

const VARIANTS = {
  pending: {
    badge: <Badge tone="attention">Pending review</Badge>,
    title: "Awaiting admin verification",
    sub: "We received your team selection. An admin reviews and verifies your role within 1 business day. Once approved, a team code appears here for your athletes to enter, which links them to your roster.",
  },
  verified: {
    badge: <Badge tone="outline" dot="#1f9d55">Verified</Badge>,
    title: "You're verified",
    sub: "Share this team code with your athletes. They enter it when they sign up, or any time from their profile, to link to your roster.",
  },
  rejected: {
    badge: <Badge tone="critical">Not approved</Badge>,
    title: "Verification not approved",
    sub: "Email hello@cachescore.com to talk it through, or update your team selection and send it again.",
  },
  not_submitted: {
    badge: <Badge tone="passive">Not started</Badge>,
    title: "Finish setting up",
    sub: "Pick your sport, league and team so an admin can verify your role.",
  },
};

// Phones (Figma 2466:19259, inside Coach Mode's phone theme). The shell's
// main pads every page (px-6 pt-5), so the page starts right under the top
// bar with -mt-5, where Figma puts the hero. While verification is pending
// the shell shows the top bar only, and its main is a full-height column
// ending with pb-8: the page takes the rest of it (flex-1) and cancels the
// pb-8, and its column is an `ob-frame`, so Actions become the white footer
// pinned to the bottom edge (onboarding.css); `ob-frame--wide` gives it the
// top rule 09 draws. The page also keeps its own dvh floor, the same height
// as the column where dvh is supported; the column's vh fallback covers the
// rest. Once the coach is verified the bottom nav is back, so the actions
// stay in the flow above it. Loading and a failed load use the same page
// box, so nothing jumps when the data arrives; their text sits 20px under
// the top bar.
const PHONE_PAGE = "-mt-5 flex w-full flex-col";
const PHONE_PAGE_PINNED = "-mt-5 -mb-8 flex min-h-[calc(100dvh-55px)] w-full flex-1 flex-col";
const PHONE_BODY = "flex flex-1 flex-col gap-[18px] pt-2";
const PHONE_BODY_PINNED = `ob-frame ob-frame--wide ${PHONE_BODY}`;
const PHONE_STATE = "flex flex-1 flex-col pt-5";

const TeamCode = ({ code }) => (
  <div className="flex flex-col gap-[10px]">
    <Overline>Team code</Overline>
    <TeamCodeBox code={code} size="lg" />
  </div>
);

const CoachVerificationPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedStatus = useSelector((s) => s.user?.user?.coachVerification?.status || null);
  const phone = usePhoneTheme();
  // The same test the Coach Mode shell uses to show its top bar only.
  const topOnly = useSelector((s) => awaitingCoachVerification(s.user?.user || {}));
  const pinned = phone && topOnly;
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [reopening, setReopening] = useState(false);
  const liveRef = useRef(true);
  useEffect(() => () => {
    liveRef.current = false;
  }, []);

  const load = useCallback(async () => {
    try {
      const next = await getCoachOnboarding();
      if (!liveRef.current) return;
      setState(next);
      setError("");
    } catch (e) {
      // A failed check keeps what is on screen.
      if (liveRef.current) setError(e.message || "We could not load your verification.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const status = state?.verification?.status || null;
  useEffect(() => {
    if (status !== "pending") return undefined;
    const timer = setInterval(load, POLL_MS);
    window.addEventListener("focus", load);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", load);
    };
  }, [status, load]);

  // The signed-in user carries the status the route guards read.
  useEffect(() => {
    if (!status || status === "not_submitted" || status === storedStatus) return;
    const email = localStorage.getItem("myEmail");
    if (email) dispatch(fetchUserByEmail(email));
  }, [status, storedStatus, dispatch]);

  // Reopens the wizard with the saved picks: on Team to change the pending
  // team, on Sport after a rejection (the sport or tier may be what was wrong).
  const changeTeam = async (step) => {
    setReopening(true);
    try {
      await saveCoachProgress(step);
      navigate("/onboarding");
    } catch (e) {
      setError(e.message || "We could not reopen your setup. Try again.");
      setReopening(false);
    }
  };
  const saveAndExit = () => {
    toast.success("You are logged out. Log in any time to check your verification.");
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const phonePage = pinned ? PHONE_PAGE_PINNED : PHONE_PAGE;

  if (!state) {
    const notice = error ? <Note tone="critical">{error}</Note> : <Loading label="Loading your verification..." />;
    if (phone) {
      return (
        <div className={phonePage}>
          <div className={PHONE_STATE}>{notice}</div>
        </div>
      );
    }
    return <div className="mx-auto w-full max-w-[560px] pt-8">{notice}</div>;
  }

  const { team } = state;
  const variant = VARIANTS[status] || VARIANTS.pending;
  const rows = verificationRows(team);
  const toProfile = () => navigate("/coach/profile");
  const exitBtn = <TextBtn onClick={saveAndExit}>Save and exit</TextBtn>;
  const actions = {
    pending: (
      <>
        <PrimaryBtn onClick={toProfile}>Go to my profile</PrimaryBtn>
        {exitBtn}
      </>
    ),
    verified: (
      <>
        <PrimaryBtn onClick={() => navigate("/coach/home")}>Open Coach Mode</PrimaryBtn>
        <TextBtn onClick={toProfile}>Go to my profile</TextBtn>
      </>
    ),
    rejected: (
      <>
        <PrimaryBtn loading={reopening} onClick={() => changeTeam("sport")}>
          Update my team
        </PrimaryBtn>
        <TextBtn onClick={toProfile}>Go to my profile</TextBtn>
        {exitBtn}
      </>
    ),
    not_submitted: (
      <>
        <PrimaryBtn onClick={() => navigate("/onboarding")}>Continue setup</PrimaryBtn>
        {exitBtn}
      </>
    ),
  };

  let pageClass = "mx-auto flex w-full max-w-[560px] flex-col pb-[96px]";
  let bodyClass = "flex flex-col gap-[28px] pt-8";
  if (phone) {
    pageClass = phonePage;
    bodyClass = pinned ? PHONE_BODY_PINNED : PHONE_BODY;
  }

  return (
    <div className={pageClass}>
      <VerificationHero
        phone={phone}
        initials={initialsOf(state.firstName, state.lastName)}
        badge={variant.badge}
        name={`${state.firstName} ${state.lastName}`.trim() || state.email}
        sub={team ? [team.name, team.requested ? team.location : leagueOf(team)].filter(Boolean).join(" · ") : ""}
      />
      <div className={bodyClass}>
        <TitleBlock eyebrow="Verification" title={variant.title} sub={variant.sub} />
        {status === "verified" && <TeamCode code={state.verification.code} />}
        {rows.length > 0 && (
          <div className="flex flex-col gap-[10px]">
            <div className="flex items-center justify-between gap-4">
              <Overline>Team settings</Overline>
              {status === "pending" && (
                <button type="button" onClick={() => changeTeam("team")} disabled={reopening} className="bm-label-l text-bm-orange hover:underline disabled:opacity-50">
                  Change team
                </button>
              )}
            </div>
            <SettingsCard>
              {rows.map((row) => (
                <KeyValueRow key={row.label} label={row.label} badge={row.requested ? <Badge tone="attention">Being added</Badge> : null}>
                  {row.value}
                </KeyValueRow>
              ))}
            </SettingsCard>
          </div>
        )}
        {status !== "verified" && status !== "not_submitted" && <Note>{LOCKED_NOTE}</Note>}
        {error && <Note tone="critical">{error}</Note>}
        <Actions>{actions[status] || actions.pending}</Actions>
      </div>
    </div>
  );
};

export default CoachVerificationPage;
